/**
 * useShapeRegistry.js
 * Manages shape creation, tracking, and synchronization with Pinia store
 *
 * NEW ARCHITECTURE:
 * - Uses non-reactive ShapeRegistry class internally
 * - Watches shapesVersion (single number) instead of deep watching shapes array
 * - Maintains same public API for backward compatibility with MeasureMap.vue
 */

import { ref, watch, computed } from 'vue'
import { cloneDeep, defaults } from 'lodash'
import { useMainStore } from '@/stores/main'
import { NEW_AREA } from '../../../constants'
import { ShapeRegistry } from '../ShapeRegistry'
import { registerShapeEvents } from '../utils/shape-event-handlers'
import { getBounds, getNewPaths, waitForMap } from '../utils/map-helpers'

export function useShapeRegistry(map, setCursor) {
  const mainStore = useMainStore()

  // NEW: Use non-reactive ShapeRegistry class internally
  let shapeRegistry = null

  // Computed ref to expose shapes array for backward compatibility
  // This triggers when shapesVersion changes
  const initedShapes = computed(() => {
    // Force reactivity on shapesVersion
    mainStore.shapesVersion // eslint-disable-line no-unused-expressions

    return shapeRegistry ? shapeRegistry.getAllShapes() : []
  })

  /**
   * Initialize registry once map is available
   */
  const initRegistry = () => {
    if (map.value && !shapeRegistry) {
      shapeRegistry = new ShapeRegistry(map.value, mainStore.commandQueue)
      console.log('ShapeRegistry initialized')
    }
  }

  /**
   * Get shape instance by indexID
   * @param {number} id - Shape indexID
   * @returns {MapShape|undefined}
   */
  const getShapeById = (id) => {
    return shapeRegistry ? shapeRegistry.getShape(id) : undefined
  }

  /**
   * Get bounds encompassing all shapes
   * @returns {google.maps.LatLngBounds|null}
   */
  const getShapeBounds = () => {
    if (map.value && shapeRegistry && shapeRegistry.count > 0) {
      return getBounds(shapeRegistry.getAllShapes())
    }
    return null
  }

  /**
   * Initialize a new shape instance
   * @param {Object} params - Configuration
   * @param {Object} params.loadState - Saved shape state to restore
   * @param {Object} params.options - Shape creation options
   * @returns {MapShape|null}
   */
  const initShape = ({ loadState = {}, options = {} }) => {
    // Safety check: ensure map and registry are initialized
    if (!map.value || !shapeRegistry) {
      console.warn('initShape: map/registry not initialized yet, deferring shape creation')
      return null
    }

    // Get current map zoom for shape initialization
    const currentZoom = mainStore.mapZoom?.number || 19

    defaults(options, {
      initialZoom: loadState.initialZoom || currentZoom,
      zoom: currentZoom
    })

    const type = loadState.type || options.type || 'area'

    // Generate default paths if not provided
    if (!options.paths && !loadState.paths) {
      options.paths = getNewPaths(map.value, type)
    }

    // Merge loadState and options
    const config = cloneDeep(Object.assign(loadState, options))

    // Create shape via registry (which handles commandQueue injection)
    const shape = shapeRegistry.createShape(type, config)

    // Register event handlers (still needed for UI interactions)
    registerShapeEvents(shape, mainStore, setCursor, initedShapes)

    return shape
  }

  /**
   * Synchronize store shapes with component shape instances
   * NEW: Simplified to delegate to ShapeRegistry's efficient sync
   */
  const syncShapesWithStore = async () => {
    // Ensure map and registry are ready
    if (!map.value) {
      await waitForMap(map)
      initRegistry()
    }

    if (!shapeRegistry) {
      initRegistry()
    }

    if (!shapeRegistry) {
      console.warn('syncShapesWithStore: registry not available')
      return
    }

    // Handle NEW_AREA placeholders (create temporary shapes)
    for (const [indexID, shapeState] of mainStore.shapesData.entries()) {
      if (shapeState.__isNewArea === true) {
        const tempShape = initShape({ options: shapeState })
        if (tempShape) {
          const shapeCfg = tempShape.getState()
          mainStore.initNewShape(shapeCfg)
          mainStore.setCurrentShape(shapeCfg)
        }
      }
    }

    // Delegate efficient syncing to ShapeRegistry
    // This handles create/update/delete in optimal way
    shapeRegistry.syncWithStore(mainStore.shapesData)

    // Re-register event handlers for any new shapes
    // (ShapeRegistry creates shapes, but doesn't handle Vue-specific events)
    shapeRegistry.getAllShapes().forEach(shape => {
      // Check if events already registered (has a flag we can check)
      if (!shape._eventsRegistered) {
        registerShapeEvents(shape, mainStore, setCursor, initedShapes)
        shape._eventsRegistered = true
      }
    })
  }

  /**
   * NEW: Watch shapesVersion instead of deep watching shapes array
   * This prevents recursion and dramatically improves performance
   */
  watch(
    () => mainStore.shapesVersion,
    () => {
      syncShapesWithStore()
    },
    { immediate: true }
  )

  /**
   * Also watch for map initialization
   */
  watch(
    map,
    (newMap) => {
      if (newMap) {
        initRegistry()
        syncShapesWithStore()
      }
    },
    { immediate: true }
  )

  return {
    initedShapes,
    getShapeById,
    getShapeBounds,
    initShape,
    syncShapesWithStore
  }
}
